import {
	ApplicationRef,
	ChangeDetectorRef,
	DestroyRef,
	Directive,
	DoCheck,
	EmbeddedViewRef,
	Host,
	inject,
	input,
	Input,
	OnDestroy,
	OnInit,
	Optional,
	SkipSelf,
	TemplateRef,
	ViewContainerRef,
} from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { AbstractScreen } from '../components/abstract-screen.js'
import { FrontendLanguageBridge } from '../bridges/frontend-language-bridge.js'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { DevBarService } from '../services/dev-bar.service.js'
import { FormCanDeactivate } from './form-can-deactivate.js'

class FormDirectiveContext {
	constructor(
		public readonly $implicit: FormDirective,
		public readonly form?: FormGroup,
	) {}
}

@Directive({
	selector: '[lowgileForm]',
	standalone: false,
})
export class FormDirective implements DoCheck, OnInit, OnDestroy {
	private devBarService = inject(DevBarService)
	private appRef = inject(ApplicationRef)
	// private screen = inject(AbstractScreen)
	private _formName: string = '' // TODO: change, as formName is not really used. !!formName could be exchanged with !_screen
	private _screen?: AbstractScreen

	private form: FormGroup | undefined
	private forms?: FormGroup[] = undefined
	viewRef?: EmbeddedViewRef<FormDirectiveContext>
	private cdTimeout: any

	constructor(
		private templateRef: TemplateRef<any>,
		private viewContainer: ViewContainerRef,
		private destroyRef: DestroyRef,
		private cdRef: ChangeDetectorRef,
		@Host() @Optional() @SkipSelf() private parentDirective: FormDirective,
	) {}

	@Input() set lowgileForm(formName: string) {
		if (formName != this._formName) {
			this.forms = undefined
		}
		this._formName = formName
	}

	@Input() @Optional() set lowgileFormScreen(screen: AbstractScreen) {
		this._screen = screen
	}

	lowgileFormPreventNavigation = input<boolean>(false)

	ngOnInit(): void {
		this.viewRef = this.viewContainer.createEmbeddedView(this.templateRef)
	}

	ngOnDestroy(): void {
		// console.log('destroy form', this._formName)
		FormCanDeactivate.unregisterForm(this.form!)
	}

	ngDoCheck() {
		const forms = this.getForms()
		Object.assign(
			this.viewRef!.context,
			new FormDirectiveContext(this, forms.at(-1)),
		)
	}

	getForms(): FormGroup[] {
		if (!this.forms) {
			this.form = undefined
			if (this._formName) {
				const formGroup = new FormGroup({})
				this.form = new Proxy(formGroup, {
					get: (target, p, receiver) => {
						if (this.devBarService.isIgnoreValidations()) {
							if (p == 'valid') return true
							if (p == 'invalid') return false
						}
						const value = Reflect.get(target, p, receiver)

						if (typeof value === 'function') {
							if (
								[
									'markAsPristine',
									'markAllAsDirty',
									'markAsDirty',
									'markAllAsTouched',
									'markAsTouched',
									'markAsUntouched',
									'reset',
								].includes(p as string)
							) {
								// Ensure change detection afterwards (e.g. for form.markAsPristine())
								return (...args: any[]) => {
									const returnValue = value.apply(receiver, args)
									this.cdRef.detectChanges()
									return returnValue
								}
							}
						}
						return value
					},
				})
				formGroup.statusChanges
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe(() => this.scheduleChangeDetection())
				if (this.lowgileFormPreventNavigation()) {
					FormCanDeactivate.registerForm(this.form)
				}
			}

			this.forms = [
				...(this._screen?.$formDirective?.getForms() ?? []),
				...(this.parentDirective?.getForms() ?? []),
				...(this.form ? [this.form] : []),
			]

			if (this.form) {
				;(globalThis as any)[this._formName] = this.form
			}

			if (this.form) {
				FrontendLanguageBridge.languageChanged$
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe(() => this.form!.updateValueAndValidity())
			}
			// console.log('forms', this.forms)
		}

		return this.forms
	}

	unregisterFormControl(formControl: FormControl) {
		const forms = this.getForms()
		for (const form of forms) {
			for (const key in form.controls) {
				if (form.controls[key] == formControl) {
					form.removeControl(key)
					this.revalidateFormControls()
					break
				}
			}
		}
		this.scheduleChangeDetection()
	}

	registerFormControl(controlPropertyName: string, formControl: FormControl) {
		if (this.form) {
			if (this.form.controls[controlPropertyName] === formControl) return

			// console.count('registerControl')
			this.form.setControl(controlPropertyName, formControl)
			this.scheduleChangeDetection()
			setTimeout(() => this.revalidateFormControls(), 10) // delay to ensure all signal values have updated; otherwise some signals could be overwritten by the valueChange event of the form controls
		}
		if (this.parentDirective) {
			this.parentDirective.registerFormControl(controlPropertyName, formControl)
		} else if (this._screen) {
			this._screen.$formDirective?.registerFormControl(
				controlPropertyName,
				formControl,
			)
		}
	}

	revalidateFormControls() {
		const forms = this.getForms()
		for (const form of forms) {
			for (const key in form.controls) {
				form.controls[key].updateValueAndValidity()
			}
		}
	}

	scheduleChangeDetection() {
		if (this.cdTimeout) return
		this.cdTimeout = setTimeout(() => {
			// don't run detectChanges here as it would flicker; just use this to ensure detectChanges is called only once
			this.cdTimeout = undefined
		})
		this.cdRef.detectChanges()

		if (this.parentDirective) {
			this.parentDirective.scheduleChangeDetection()
		} else if (this._screen) {
			this._screen.$formDirective?.scheduleChangeDetection()
		}
	}
}
